/**
 * The note counter — count cash one denomination at a time.
 *
 * Used at both ends of a handover: the collector counts the notes he is handing
 * over (that tally is his declared amount), and the admin counts the notes he
 * receives (that tally is the amount counted). The same component on both sides
 * so the two speak the same six denominations, in the same order.
 *
 * It holds no state of its own — the caller owns the `{denomination: count}`
 * map — because both callers need the running total to drive other UI (the
 * declared amount, the variance banner) and a component that hid the counts
 * would force them to recompute what it already knows.
 */

import { Card, TextInput } from './ui';
import { money } from '../lib/format';

/** Indian cash denominations, largest first — must match the API's list. */
export const NOTE_DENOMINATIONS = [500, 200, 100, 50, 20, 10] as const;

export type NoteCounts = Record<number, number>;

/** The counted total, in integer paise. */
export function noteTotalPaise(notes: NoteCounts): number {
  return NOTE_DENOMINATIONS.reduce((sum, d) => sum + d * (notes[d] ?? 0), 0) * 100;
}

/** The counter's map as the API's denominations array (zero rows dropped). */
export function toDenominations(notes: NoteCounts): { denomination: number; noteCount: number }[] {
  return NOTE_DENOMINATIONS
    .filter((d) => (notes[d] ?? 0) > 0)
    .map((d) => ({ denomination: d, noteCount: notes[d]! }));
}

interface Props {
  notes: NoteCounts;
  onChange: (next: NoteCounts) => void;
  title?: string;
  /**
   * Which denominations to show. Defaults to all six. The note-exchange sheet
   * passes a subset: only the small notes (₹50/₹20/₹10) on the "giving" side and
   * only the bank-ready notes (₹500/₹200/₹100) on the "getting" side, because a
   * swap by definition moves value from one set to the other.
   */
  denominations?: readonly number[];
}

export function NoteCounter({
  notes, onChange, title = 'Count the notes', denominations = NOTE_DENOMINATIONS,
}: Props) {
  return (
    <Card flush>
      <div className="card__header"><h3>{title}</h3></div>
      <div className="list">
        {denominations.map((d) => (
          <div key={d} className="list__item list__item--static" style={{ gap: 10 }}>
            <span className="grow t-sm t-semi">₹{d} notes</span>
            <TextInput
              numeric
              value={notes[d] ? String(notes[d]) : ''}
              placeholder="0"
              onChange={(e) => {
                const n = Math.max(0, parseInt(e.target.value.replace(/\D/g, ''), 10) || 0);
                onChange({ ...notes, [d]: n });
              }}
              style={{ maxWidth: 90, textAlign: 'right' }}
            />
            <span className="t-xs t-muted" style={{ minWidth: 72, textAlign: 'right' }}>
              {money(d * (notes[d] ?? 0) * 100)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}
